// 20 coherent sub-packages per category. Each package's title, photo theme and
// search query/booking-type are aligned with what the destination's partner
// actually sells, so every card links to a relevant, specific partner search.
import { buildPartnerUrl } from "./partnerLinks";
import { photoFromPool, poolFor } from "./themePhotos";

// Each entry: { name (display), kw (search keywords), theme (single-word photo keyword), type? (BookRetreats scopes[type]) }
const PACKAGES = {
  travel: [
    { name: "Highlights Small-Group Tour", kw: "highlights tour", theme: "city" },
    { name: "Island Hopping Adventure", kw: "island hopping", theme: "island" },
    { name: "Food & Wine Discovery", kw: "food and wine tour", theme: "food" },
    { name: "Cultural & Heritage Tour", kw: "cultural tour", theme: "architecture" },
    { name: "Coastal Sailing Trip", kw: "sailing", theme: "sailing" },
    { name: "Self-Guided Walking Tour", kw: "walking tour", theme: "trail" },
    { name: "Private Guided Experience", kw: "private tour", theme: "landmark" },
    { name: "Photography Tour", kw: "photography tour", theme: "scenic" },
    { name: "Ancient History Tour", kw: "history tour", theme: "ruins" },
    { name: "Scenic Rail Journey", kw: "rail journey tour", theme: "train" },
    { name: "Active Explorer Tour", kw: "active adventure tour", theme: "hiking" },
    { name: "Family Discovery Tour", kw: "family tour", theme: "family" },
    { name: "Luxury Escorted Tour", kw: "luxury tour", theme: "luxury" },
    { name: "Honeymoon Romance Tour", kw: "romantic tour", theme: "sunset" },
    { name: "Wine Country Tour", kw: "wine tour", theme: "vineyard" },
    { name: "Coast & Beaches Tour", kw: "beach tour", theme: "beach" },
    { name: "Mountains & Lakes Tour", kw: "mountains lakes tour", theme: "mountain" },
    { name: "Local Villages Tour", kw: "village tour", theme: "village" },
    { name: "Markets & Gastronomy Tour", kw: "market food tour", theme: "market" },
    { name: "Grand Highlights Tour", kw: "grand tour", theme: "panorama" },
  ],
  yoga: [
    { name: "Vinyasa Flow Retreat", kw: "vinyasa yoga retreat", theme: "yoga", type: "Active Yoga Retreats" },
    { name: "Meditation & Mindfulness", kw: "meditation retreat", theme: "meditation", type: "Spiritual Retreats" },
    { name: "Yin & Restorative Yoga", kw: "yin yoga retreat", theme: "yoga", type: "Yoga Retreats" },
    { name: "Detox & Cleanse Week", kw: "detox retreat", theme: "spa", type: "Detox Retreats" },
    { name: "Ayurveda Healing Retreat", kw: "ayurveda retreat", theme: "wellness", type: "Ayurvedic Retreats" },
    { name: "Beginner's Yoga Escape", kw: "beginner yoga retreat", theme: "yoga", type: "Yoga Retreats for Beginners" },
    { name: "Couples Yoga & Spa", kw: "couples retreat", theme: "spa", type: "Couples Tantra Retreats" },
    { name: "Beachfront Yoga Retreat", kw: "beachfront yoga retreat", theme: "beach", type: "Beachfront Yoga Retreats" },
    { name: "Wellness Reset Retreat", kw: "wellness retreat", theme: "wellness", type: "Wellness Retreats" },
    { name: "Sound & Energy Healing", kw: "healing retreat", theme: "nature", type: "Healing Retreats" },
    { name: "Spiritual Awakening", kw: "spiritual retreat", theme: "temple", type: "Spiritual Retreats" },
    { name: "All-Inclusive Yoga Week", kw: "all inclusive yoga retreat", theme: "yoga", type: "All-Inclusive Yoga Retreats" },
    { name: "Chakra Balancing Retreat", kw: "chakra retreat", theme: "meditation", type: "Chakra Retreats" },
    { name: "Family Yoga Escape", kw: "family retreat", theme: "family", type: "Family Retreats" },
    { name: "Luxury Wellness Retreat", kw: "luxury yoga retreat", theme: "luxury", type: "Luxury Retreats" },
    { name: "Affordable Yoga Week", kw: "affordable yoga retreat", theme: "yoga", type: "Affordable Yoga Retreats" },
    { name: "Detox & Yoga Reset", kw: "detox yoga retreat", theme: "spa", type: "Detox Retreats" },
    { name: "Beach Yoga & Sun", kw: "beach yoga retreat", theme: "beach", type: "Beach Retreats" },
    { name: "Restorative Wellness", kw: "restorative retreat", theme: "wellness", type: "Wellness Retreats" },
    { name: "Holistic Healing Week", kw: "holistic healing retreat", theme: "nature", type: "Healing Retreats" },
  ],
  wellness: [
    { name: "Sunset Catamaran Cruise", kw: "sunset catamaran cruise", theme: "sailing" },
    { name: "Beach Club Day Pass", kw: "beach club day pass", theme: "beach" },
    { name: "Spa & Thermal Baths", kw: "spa thermal baths", theme: "spa" },
    { name: "Wine Tasting Experience", kw: "wine tasting", theme: "wine" },
    { name: "Boat Party Cruise", kw: "boat party cruise", theme: "boat" },
    { name: "Pool & Cocktail Day", kw: "pool day pass", theme: "pool" },
    { name: "Hammam & Massage", kw: "hammam massage", theme: "spa" },
    { name: "Rooftop Bar Tour", kw: "rooftop bar", theme: "rooftop" },
    { name: "Private Yacht Charter", kw: "private yacht charter", theme: "yacht" },
    { name: "Sailing Day Trip", kw: "sailing day trip", theme: "sailing" },
    { name: "Wellness & Spa Day", kw: "spa day", theme: "wellness" },
    { name: "Sunset Sailing & Drinks", kw: "sunset sailing", theme: "sunset" },
    { name: "VIP Club Night", kw: "nightclub vip", theme: "nightlife" },
    { name: "Snorkeling & Beach Day", kw: "snorkeling tour", theme: "snorkeling" },
    { name: "Food & Tapas Tour", kw: "food tapas tour", theme: "food" },
    { name: "Cocktail Masterclass", kw: "cocktail class", theme: "cocktail" },
    { name: "Catamaran with Lunch", kw: "catamaran lunch cruise", theme: "boat" },
    { name: "Hot Springs Visit", kw: "hot springs", theme: "springs" },
    { name: "Sunset Viewpoint Tour", kw: "sunset viewpoint tour", theme: "sunset" },
    { name: "Luxury Day Spa", kw: "luxury day spa", theme: "spa" },
  ],
  adventure: [
    { name: "Guided Summit Trek", kw: "summit trek", theme: "summit" },
    { name: "Via Ferrata Day", kw: "via ferrata tour", theme: "climbing" },
    { name: "Hut-to-Hut Trek", kw: "hut to hut trek", theme: "mountain" },
    { name: "Glacier Hike", kw: "glacier hike", theme: "glacier" },
    { name: "Kayaking Day Tour", kw: "kayaking tour", theme: "kayak" },
    { name: "Sunrise Summit Tour", kw: "sunrise hike tour", theme: "sunrise" },
    { name: "Trail Running Camp", kw: "trail running", theme: "trail" },
    { name: "Photography Hiking Tour", kw: "photography hiking tour", theme: "scenic" },
    { name: "Family Adventure Day", kw: "family adventure tour", theme: "family" },
    { name: "Snowshoe Expedition", kw: "snowshoe tour", theme: "snow" },
    { name: "Rock Climbing Day", kw: "rock climbing tour", theme: "climbing" },
    { name: "Mountain Biking Tour", kw: "mountain biking tour", theme: "biking" },
    { name: "Wildlife Safari Walk", kw: "wildlife tour", theme: "wildlife" },
    { name: "Canyoning Adventure", kw: "canyoning tour", theme: "canyon" },
    { name: "Paragliding Tandem", kw: "paragliding tandem", theme: "paragliding" },
    { name: "Multi-Day Hiking Tour", kw: "hiking tour", theme: "hiking" },
    { name: "White-Water Rafting", kw: "rafting tour", theme: "river" },
    { name: "Caving Expedition", kw: "caving tour", theme: "cave" },
    { name: "Coastal Cliff Walk", kw: "coastal hike tour", theme: "coast" },
    { name: "Volcano Crater Hike", kw: "volcano hike tour", theme: "volcano" },
  ],
};

const PRICE_STEPS = [320, 480, 540, 620, 720, 820, 920, 1020, 1140, 1280, 1420, 1580, 1720, 1880, 2100, 2380, 2640, 2960, 3280, 3640];
const DURATIONS = ["2 days","3 days","3 days","4 days","4 days","5 days","5 days","6 days","6 days","7 days","7 days","7 days","8 days","8 days","9 days","10 days","10 days","12 days","14 days","14 days"];

export function getSubRetreats(parent) {
  const pool = PACKAGES[parent.category] || PACKAGES.travel;
  const counters = {};
  return pool.map((p, i) => {
    const pname = poolFor(p.theme);
    const idx = counters[pname] = (counters[pname] ?? -1) + 1;
    return {
    id: `${parent.id}-sub-${i + 1}`,
    title: p.name,
    location: `${parent.title}, ${parent.country}`,
    placeTitle: parent.title,
    country: parent.country,
    category: parent.category,
    query: p.kw,
    bookType: p.type,
    blurb: `${p.name} in ${parent.title}. Hand-picked by YogaToYacht.`,
    duration: DURATIONS[i],
    priceFrom: `€${PRICE_STEPS[i].toLocaleString()}`,
    image: photoFromPool(p.theme, idx),
    partner: parent.partner,
    link: buildPartnerUrl({
      partner: parent.partner,
      title: parent.title,
      country: parent.country,
      query: p.kw,
      bookType: p.type,
    }),
  };
  });
}
